// src/components/resources/BreadcrumbTrail.tsx

import type { ComponentType, ReactNode } from 'react'
import Link from 'next/link'
import RiArrowRightSLine from '~/icons/remix/generated/RiArrowRightSLine'
import RiHome3Line from '~/icons/remix/generated/RiHome3Line'
import RiArrowLeftLine from '~/icons/remix/generated/RiArrowLeftLine'
import type { BreadcrumbItem } from '~/lib/resources/breadcrumbs'

/**
 * Props for the injectable crumb hover wrapper.
 */
export interface CrumbHoverWrapperProps {
  /** The crumb link to wrap. */
  children: ReactNode
}

/**
 * Default hover wrapper — a plain `<div>` with no client behavior.
 *
 * Renders the exact same element the client shell's `motion.div` produces at
 * rest (motion emits a bare `<div>` in SSR when only `whileHover` is set), so
 * the static and motion-enhanced trails are markup-identical; the motion
 * variant simply adds the hover-scale effect after hydration.
 */
function PlainCrumbHoverWrapper({ children }: CrumbHoverWrapperProps): ReactNode {
  return <div>{children}</div>
}

/**
 * Props for {@link BreadcrumbTrail}.
 */
interface BreadcrumbTrailProps {
  /**
   * Ordered breadcrumb items starting at the first section BELOW `/resources`
   * (e.g. Glossary → category → term). The trail's own home-icon crumb is the
   * single `/resources` link, so `buildResourceBreadcrumbItems()` deliberately
   * omits a leading `resources` item — the `/resources` hub derives `[]` and
   * renders the home icon alone (dedupe tracked in #541).
   */
  items: BreadcrumbItem[]
  /**
   * Wrapper rendered around each non-current crumb link. Defaults to a plain
   * `<div>` (server-safe, zero client JS). The client breadcrumb shell
   * (`Breadcrumbs`) injects a `motion.div` hover-scale wrapper here so the
   * motion dependency stays out of server-rendered subtrees (e.g. the glossary
   * term HydrationGate fallback).
   */
  HoverWrapper?: ComponentType<CrumbHoverWrapperProps>
}

/**
 * Presentational breadcrumb trail — mobile back-link + desktop anchor list.
 *
 * Purely props → markup: no state, no effects, no fetching, and — deliberately —
 * no `'use client'` directive: this module is server-safe, so server components
 * (like the glossary term page's crawler-first HydrationGate fallback in
 * `GlossaryTermServerContent`) can render the trail as static first-pass HTML
 * without pulling `motion/react` or any client bundle into that subtree.
 * Crawlers see the full child → parent link path
 * (term → category → glossary → resources) without executing JavaScript.
 *
 * The client scroll-border shell (`Breadcrumbs` in `Breadcrumbs.tsx`) renders
 * this same component with a `motion.div` hover wrapper injected via
 * `HoverWrapper`, keeping motion strictly a client-shell enhancement.
 *
 * @param props - Component props.
 * @param props.items - Precomputed breadcrumb items.
 * @param props.HoverWrapper - Optional wrapper around non-current crumb links.
 * @returns The breadcrumb trail markup.
 */
export function BreadcrumbTrail({
  items,
  HoverWrapper = PlainCrumbHoverWrapper
}: BreadcrumbTrailProps): ReactNode {
  // For mobile, only show current and previous level
  const mobileBreadcrumbs = items.slice(-2)
  const previousPage = mobileBreadcrumbs.length > 1 ? mobileBreadcrumbs[0] : null

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Mobile View */}
      <nav aria-label="Breadcrumb" className="md:hidden py-4">
        <div className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
          <Link
            href={previousPage?.href ?? '/resources'}
            className="flex items-center"
          >
            <RiArrowLeftLine className="mr-2 h-4 w-4" />
            <span>
              Back to {previousPage?.label ?? 'Resources'}
            </span>
          </Link>
        </div>
      </nav>

      {/* Desktop View */}
      <nav aria-label="Breadcrumb" className="hidden md:block py-5">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <HoverWrapper>
              <Link
                href="/resources"
                className="flex items-center text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <RiHome3Line className="h-4 w-4" />
                <span className="sr-only">Resources</span>
              </Link>
            </HoverWrapper>
          </li>

          {items.map((item, index) => {
            const isCurrent = index === items.length - 1
            return (
              <li key={item.href} className="flex items-center">
                <RiArrowRightSLine className="h-4 w-4 text-gray-400" />
                {isCurrent ? (
                  // The current page is not a useful link to itself — render it as
                  // plain text with aria-current so AT announces "current page"
                  // (and no redundant self-link / hover affordance).
                  <span
                    aria-current="page"
                    className="ml-2 font-medium text-gray-900 dark:text-white"
                  >
                    {item.label}
                  </span>
                ) : (
                  <HoverWrapper>
                    <Link
                      href={item.href}
                      className="ml-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                      {item.label}
                    </Link>
                  </HoverWrapper>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </div>
  )
}
